import { useInfiniteQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { getApiClient } from '../api/apiClient';

const apiClient = getApiClient('kisanVani');

export const usePosts = (
  limit = 10,
  status = 'all',
  dateFilter = null,
  showAllData = false,
  page = 1,
  sortBy = 'createdAt',
  sortOrder = 'desc'
) => {
  const queryClient = useQueryClient();

  const verifyPost = async (postId, status, remarks = null) => {
    try {
      const updatedBy = localStorage.getItem('userName') || 'User';
      const updatedById = localStorage.getItem('userId');
      const payload = { postId, status, updatedBy };
      if (updatedById) {
        payload.updatedById = updatedById;
      }
      if (status === 'rejected' && remarks) {
        payload.remarks = remarks;
      }
      const response = await apiClient.post('/approve/posts', payload);
      
      if (response.data?.success) {
        // Refresh every posts query (lists and dashboard status counts)
        queryClient.invalidateQueries({ queryKey: ['posts'] });
        return response.data;
      }
      throw new Error('Failed to verify post');
    } catch (error) {
      throw error;
    }
  };

  const fetchAllPosts = async () => {
    let url = `/posts/all?status=${status}`;
    
    // Add date parameters if they exist
    if (dateFilter?.fromDate) {
      url += `&fromDate=${encodeURIComponent(dateFilter.fromDate)}`;
    }
    if (dateFilter?.toDate) {
      url += `&toDate=${encodeURIComponent(dateFilter.toDate)}`;
    }
    // Sorting
    if (sortBy) {
      url += `&sortBy=${encodeURIComponent(sortBy)}`;
    }
    if (sortOrder) {
      url += `&sortOrder=${encodeURIComponent(sortOrder)}`;
    }

    // If showing all data, don't use pagination
    if (showAllData) {
      url += '&limit=1000'; // Use a large limit to get all data
    } else {
      url += `&page=${page}&limit=${limit}`;
    }

    const response = await apiClient.get(url);
    
    if (!response.data?.success || !Array.isArray(response.data?.data?.items)) {
      throw new Error('Invalid data format received');
    }

    // If showing all data, return all items in a single page. totalPages must be 1 here:
    // reporting the server's value makes hasNextPage true, and fetchNextPage would then
    // refetch this same non-paginated request over and over, appending duplicate pages.
    if (showAllData) {
      return {
        items: response.data.data.items,
        currentPage: 1,
        totalPages: 1,
        totalItems: response.data.data.items.length
      };
    }

    // Return the paginated data with metadata
    return {
      items: response.data.data.items,
      currentPage: response.data.data.page,
      totalPages: response.data.data.totalPages,
      totalItems: response.data.data.totalCount,
      limit: response.data.data.limit
    };
  };

  return {
    ...useInfiniteQuery({
      queryKey: [
        'posts',
        status,
        dateFilter?.fromDate ?? null,
        dateFilter?.toDate ?? null,
        showAllData,
        page,
        sortBy,
        sortOrder
      ],
      queryFn: async ({ pageParam = page }) => {
        // If showing all data or have date filter, fetch all data at once
        if (showAllData || dateFilter) {
          return fetchAllPosts();
        }

        // Otherwise use normal pagination
        let url = `/posts/all?page=${pageParam}&limit=${limit}&status=${status}`;
        if (sortBy) {
          url += `&sortBy=${encodeURIComponent(sortBy)}`;
        }
        if (sortOrder) {
          url += `&sortOrder=${encodeURIComponent(sortOrder)}`;
        }
        const response = await apiClient.get(url);
        
        if (!response.data?.success || !Array.isArray(response.data?.data?.items)) {
          throw new Error('Invalid data format received');
        }
        
        // Return the paginated data with metadata
        return {
          items: response.data.data.items,
          currentPage: response.data.data.page,
          totalPages: response.data.data.totalPages,
          totalItems: response.data.data.totalCount,
          limit: response.data.data.limit
        };
      },
      getNextPageParam: (lastPage) => {
        if (lastPage.currentPage < lastPage.totalPages) {
          return lastPage.currentPage + 1;
        }
        return undefined;
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage.currentPage > 1) {
          return firstPage.currentPage - 1;
        }
        return undefined;
      },
      // Keep showing the previous data while a new date filter loads (avoids the chart blanking out)
      placeholderData: showAllData ? keepPreviousData : undefined,
      staleTime: 5 * 60 * 1000, // Data is considered fresh for 5 minutes
      cacheTime: 30 * 60 * 1000, // Cache is kept for 30 minutes
    }),
    verifyPost
  };
}; 